/**
 * Declarative sub-navigation for agent context (paths must match vue-router).
 * Primary/secondary sidebar items live in sidebarLinks.js; this file adds
 * nested items that are not in the main array (e.g. Contacts tabs, Settings).
 */

/** @typedef {{ label: string, path: string, routeName?: string, feature?: string, permission?: string, roles?: string[], hidden?: boolean }} NavChild */

/** @type {NavChild[]} */
export const CONTACTS_SUBNAV = [
  {
    label: 'All Contacts',
    path: '/contacts',
    routeName: 'contacts-main',
    feature: 'contacts',
  },
  {
    label: 'Views',
    path: '/contacts/views',
    routeName: 'contacts-views',
    feature: 'contacts',
  },
  {
    label: 'Archived',
    path: '/contacts/archived',
    routeName: 'contacts-archived',
    feature: 'contacts_archived',
  },
  {
    label: 'Notes',
    path: '/contacts/notes',
    routeName: 'contacts-notes',
    feature: 'contacts_notes',
  },
  {
    label: 'Tasks',
    path: '/contacts/tasks',
    routeName: 'contacts-tasks',
    feature: 'contacts_tasks',
  },
  {
    label: 'Relationships',
    path: '/contacts/relationships',
    routeName: 'contacts-relationships',
    feature: 'contacts_relationships',
  },
  {
    label: 'Boards',
    path: '/contacts/boards',
    routeName: 'contacts-boards',
    feature: 'contacts_boards',
  },
  {
    label: 'Import contacts',
    path: '/contacts/import',
    routeName: 'contacts-import',
    feature: 'contacts',
  },
  {
    label: 'New contact',
    path: '/contacts/create',
    routeName: 'contact-create',
    feature: 'contacts',
  },
];

/** @type {NavChild[]} */
export const GROUPS_SUBNAV = [
  {
    label: 'All groups',
    path: '/groups',
    routeName: 'groups',
    feature: 'groups',
  },
  {
    label: 'New group',
    path: '/groups/create',
    routeName: 'group-create',
    feature: 'groups',
  },
];

/**
 * Settings sidebar (matches Settings.vue navLinks + otherSettingLinks paths).
 * @type {NavChild[]}
 */
export const SETTINGS_SUBNAV = [
  {
    label: 'Profile',
    path: '/settings/profile',
    routeName: 'settings-profile',
    roles: ['admin', 'guest'],
  },
  {
    label: 'Users',
    path: '/settings/users',
    routeName: 'settings-users',
    feature: 'users',
    permission: 'read_users',
    roles: ['admin', 'guest'],
  },
  {
    label: 'Archived Users',
    path: '/settings/archived-users',
    routeName: 'settings-archived-users',
    feature: 'archived_users',
    permission: 'read_users',
    roles: ['admin', 'guest'],
  },
  {
    label: 'User Permissions',
    path: '/settings/permissions',
    routeName: 'settings-permissions',
    permission: 'update_users',
    feature: 'user_permissions',
    roles: ['admin', 'guest'],
  },
  {
    label: 'Teams',
    path: '/settings/teams',
    routeName: 'settings-teams',
    feature: 'teams',
    roles: ['admin'],
  },
  {
    label: 'Tags',
    path: '/settings/tags',
    routeName: 'settings-tags',
    feature: 'tags',
    roles: ['admin'],
  },
  {
    label: 'Fields',
    path: '/settings/fields',
    routeName: 'settings-fields',
    feature: 'fields',
    roles: ['admin'],
  },
  {
    label: 'Account',
    path: '/settings/account',
    routeName: 'settings-account',
    feature: 'account',
    roles: ['admin', 'guest'],
  },
  {
    label: 'Integrations',
    path: '/settings/integrations',
    routeName: 'settings-integrations',
    feature: 'integrations',
    roles: ['admin'],
  },
  {
    label: 'Portal Settings',
    path: '/settings/portal',
    routeName: 'settings-portal',
    roles: ['admin'],
  },
  {
    label: 'Links',
    path: '/settings/links',
    routeName: 'settings-links',
    permission: 'links',
    feature: 'links',
    roles: ['admin', 'guest'],
  },
  {
    label: 'Backend',
    path: '/settings/backend',
    routeName: 'settings-backend',
    hidden: true,
    roles: ['admin'],
  },
  {
    label: 'Compliance',
    path: '/settings/compliance',
    routeName: 'settings-compliance',
    hidden: true,
    roles: ['admin'],
  },
  {
    label: 'Business Information',
    path: '/settings/business',
    routeName: 'settings-business',
    feature: 'other_settings',
    roles: ['admin'],
  },
  {
    label: 'Email Templates',
    path: '/settings/email-templates',
    routeName: 'settings-email-templates',
    feature: 'email_templates',
    roles: ['admin'],
  },
  {
    label: 'Email Addresses',
    path: '/settings/email-addresses',
    routeName: 'settings-email-addresses',
    feature: 'email_addresses',
    roles: ['admin'],
  },
  {
    label: 'Conversations',
    path: '/settings/conversations',
    routeName: 'settings-conversations',
    feature: 'conversations',
    roles: ['admin'],
  },
];
